import { useState, useEffect, useCallback, useMemo } from "react";
import { axiosInstance } from "../../App";
import { downloadFileWithProgress } from "../../lib/downloadProgress";
import { toast } from "sonner";
import { 
  Calendar, Users, CheckCircle2, XCircle, Download, 
  ChevronLeft, ChevronRight, Filter, GraduationCap,
  Loader2, CalendarDays, BarChart3, Edit3
} from "lucide-react";
import { StatsCard, CalendarDay, DatePickerModal, OverrideModal } from "./attendance";

export default function AttendanceModule({ user, selectedBranch = "all" }) {
  const [loading, setLoading] = useState(true);
  const [attendanceData, setAttendanceData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedClass, setSelectedClass] = useState("");
  const [filterBranch, setFilterBranch] = useState(selectedBranch || "");
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState("list");
  const [selectedChild, setSelectedChild] = useState(null);
  const [calendarData, setCalendarData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [classes, setClasses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overrideData, setOverrideData] = useState({ child: null, date: null, status: false });
  const [savingOverride, setSavingOverride] = useState(false);
  const [attendanceFilter, setAttendanceFilter] = useState("all"); // "all", "hadir", "tak_hadir"
  const [percentageFilter, setPercentageFilter] = useState("all"); // "all", "0-19", "20-39", "40-59", "60-79", "80-100"

  const role = user?.role || "";
  const canDownload = role !== "ibu_bapa";
  const canOverride = ["superadmin", "branch_manager", "admin"].includes(role);
  // Only admin/superadmin/branch_manager can see filters
  const canFilter = ["superadmin", "branch_manager", "admin"].includes(role);
  // Guru and ibu_bapa can only see their assigned classes/children
  const isRestrictedView = ["guru", "ibu_bapa"].includes(role);

  // Get available branches - only for superadmin/branch_manager
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await axiosInstance.get("/public/branches");
        setBranches(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error fetching branches:", error);
        setBranches([]);
      }
    };
    if (role === "superadmin" || role === "branch_manager") {
      fetchBranches();
    }
  }, [role]);

  // Get available classes - only for admin/superadmin/branch_manager
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await axiosInstance.get("/classes");
        let classData = [];
        
        // Handle different response formats
        if (Array.isArray(response.data)) {
          // Direct array response
          classData = response.data.map(item => {
            if (typeof item === 'string') {
              return { name: item, id: item };
            }
            return item;
          });
        } else if (response.data?.classes && Array.isArray(response.data.classes)) {
          // Nested classes array
          classData = response.data.classes.map(item => {
            if (typeof item === 'string') {
              return { name: item, id: item };
            }
            return item;
          });
        }
        
        // Define default classes if none returned
        if (classData.length === 0) {
          classData = [
            { name: "Baby Care (2-12 months)", id: "baby-care" },
            { name: "Toddler Playgroup I (1-2 years old)", id: "toddler-1" },
            { name: "Toddler Playgroup II (2-3 years old)", id: "toddler-2" },
            { name: "Nursery Exploration (3-4 years old)", id: "nursery" }
          ];
        }
        
        setClasses(classData);
      } catch (error) {
        console.error("Error fetching classes:", error);
        // Fallback to default classes
        setClasses([
          { name: "Baby Care (2-12 months)", id: "baby-care" },
          { name: "Toddler Playgroup I (1-2 years old)", id: "toddler-1" },
          { name: "Toddler Playgroup II (2-3 years old)", id: "toddler-2" },
          { name: "Nursery Exploration (3-4 years old)", id: "nursery" }
        ]);
      }
    };
    // Only fetch classes for roles that can filter
    if (canFilter) {
      fetchClasses();
    }
  }, [canFilter]);

  // Fetch attendance data
  const fetchAttendance = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        month: selectedMonth,
        year: selectedYear
      });
      
      // Use filterBranch for superadmin/branch_manager
      if ((role === "superadmin" || role === "branch_manager") && filterBranch && filterBranch !== "all") {
        params.append("branch", filterBranch);
      }
      if (selectedClass) {
        params.append("class_name", selectedClass);
      }
      if (selectedDate) {
        params.append("date", selectedDate);
      }
      
      const response = await axiosInstance.get(`/attendance?${params}`);
      setAttendanceData(response.data);
    } catch (error) {
      console.error("Error fetching attendance:", error);
      toast.error("Gagal mendapatkan data kehadiran");
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear, filterBranch, selectedClass, selectedDate, role]);

  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  // Fetch calendar data for a specific child
  const fetchCalendarData = async (childId) => {
    try {
      const response = await axiosInstance.get(`/attendance/calendar?child_id=${childId}&month=${selectedMonth}&year=${selectedYear}`);
      setCalendarData(response.data);
      setSelectedChild(response.data.child);
    } catch (error) {
      toast.error("Gagal mendapatkan data kalendar");
    }
  };

  // Export attendance
  const exportAttendance = async (format) => {
    if (!canDownload) {
      toast.error("Anda tidak mempunyai kebenaran untuk memuat turun");
      return;
    }
    
    setExporting(true);
    try {
      const params = new URLSearchParams({
        month: selectedMonth,
        year: selectedYear,
        format: format
      });
      
      if ((role === "superadmin" || role === "branch_manager") && filterBranch && filterBranch !== "all") {
        params.append("branch", filterBranch);
      }
      if (selectedClass) {
        params.append("class_name", selectedClass);
      }
      
      // Build filename with branch and class info
      let filename = `kehadiran_${selectedYear}_${selectedMonth.toString().padStart(2, '0')}`;
      if (filterBranch && filterBranch !== "all") {
        filename += `_${filterBranch.replace(/\s+/g, '_')}`;
      }
      if (selectedClass) {
        filename += `_${selectedClass.split('(')[0].trim().replace(/\s+/g, '_')}`;
      }
      filename += `.${format === 'excel' ? 'xlsx' : 'pdf'}`;

      await downloadFileWithProgress({
        label: `Laporan Kehadiran (${format === 'excel' ? 'Excel' : 'PDF'})`,
        filename,
        request: (onDownloadProgress) => axiosInstance.get(`/attendance/export?${params}`, {
          responseType: 'blob',
          onDownloadProgress
        })
      });
      
      toast.success(`Fail ${format.toUpperCase()} berjaya dimuat turun!`);
    } catch (error) {
      toast.error("Gagal export data");
    } finally {
      setExporting(false);
    }
  };

  // Override attendance
  const handleOverride = async (newStatus) => {
    if (!overrideData.child || !overrideData.date) return;
    
    setSavingOverride(true);
    try {
      await axiosInstance.post("/attendance/override", {
        child_id: overrideData.child.id || overrideData.child.child_id,
        date: overrideData.date,
        present: newStatus
      });
      
      toast.success("Kehadiran berjaya dikemas kini");
      setShowOverrideModal(false);
      
      // Refresh data
      if (selectedChild) {
        fetchCalendarData(selectedChild.id);
      } else {
        fetchAttendance();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal mengemas kini kehadiran");
    } finally {
      setSavingOverride(false);
    }
  };

  // Month navigation
  const goToPrevMonth = () => {
    setSelectedDate(null);
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const goToNextMonth = () => {
    setSelectedDate(null);
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  // Handle date selection from picker
  const handleDateSelect = (dateStr) => {
    setSelectedDate(dateStr);
    setShowDatePicker(false);
  };

  const monthNames = [
    "Jan", "Feb", "Mac", "Apr", "Mei", "Jun",
    "Jul", "Ogo", "Sep", "Okt", "Nov", "Dis"
  ];

  const monthNamesFull = [
    "Januari", "Februari", "Mac", "April", "Mei", "Jun",
    "Julai", "Ogos", "September", "Oktober", "November", "Disember"
  ];

  // Filter attendance data (must be before any early returns for hooks rule)
  const filteredAttendance = (attendanceData?.attendance || []).filter(child => {
    // Filter by attendance status (hadir/tak hadir) - based on selected date or latest data
    if (attendanceFilter === "hadir") {
      if (selectedDate) {
        const dateData = child.dates?.[selectedDate];
        if (!dateData?.present) return false;
      } else {
        if (child.present_count === 0) return false;
      }
    } else if (attendanceFilter === "tak_hadir") {
      if (selectedDate) {
        const dateData = child.dates?.[selectedDate];
        if (dateData?.present) return false;
      } else {
        if (child.absent_count === 0) return false;
      }
    }

    // Filter by percentage range
    const rate = child.attendance_rate || 0;
    if (percentageFilter === "0-19" && (rate < 0 || rate > 19)) return false;
    if (percentageFilter === "20-39" && (rate < 20 || rate > 39)) return false;
    if (percentageFilter === "40-59" && (rate < 40 || rate > 59)) return false;
    if (percentageFilter === "60-79" && (rate < 60 || rate > 79)) return false;
    if (percentageFilter === "80-100" && (rate < 80 || rate > 100)) return false;

    return true;
  });

  // Calculate today's attendance statistics by class (memoized for performance)
  const todayStats = useMemo(() => {
    const today = selectedDate || new Date().toISOString().split('T')[0];
    const allAttendance = attendanceData?.attendance || [];
    
    const stats = {
      totalPresent: 0,
      totalAbsent: 0,
      byClass: {}
    };

    allAttendance.forEach(child => {
      const dateData = child.dates?.[today];
      const className = child.class_name || "Tiada Kelas";
      
      // Initialize class stats if not exists
      if (!stats.byClass[className]) {
        stats.byClass[className] = { present: 0, absent: 0, total: 0 };
      }

      if (dateData) {
        stats.byClass[className].total++;
        if (dateData.present) {
          stats.totalPresent++;
          stats.byClass[className].present++;
        } else {
          stats.totalAbsent++;
          stats.byClass[className].absent++;
        }
      } else {
        // No data means absent for past dates
        const isPast = today < new Date().toISOString().split('T')[0];
        if (isPast) {
          stats.totalAbsent++;
          stats.byClass[className].absent++;
          stats.byClass[className].total++;
        }
      }
    });

    return stats;
  }, [attendanceData, selectedDate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
        <span className="ml-2 text-sm text-gray-500">Memuatkan...</span>
      </div>
    );
  }

  const stats = attendanceData?.stats || {};

  // Color coding based on attendance rate
  const getAttendanceColor = (rate) => {
    if (rate <= 19) return { bg: "bg-red-50", text: "text-red-600", bar: "bg-red-500", border: "border-red-200" };
    if (rate <= 39) return { bg: "bg-orange-50", text: "text-orange-600", bar: "bg-orange-500", border: "border-orange-200" };
    if (rate <= 59) return { bg: "bg-yellow-50", text: "text-yellow-600", bar: "bg-yellow-500", border: "border-yellow-200" };
    if (rate <= 79) return { bg: "bg-blue-50", text: "text-blue-600", bar: "bg-blue-500", border: "border-blue-200" };
    return { bg: "bg-green-50", text: "text-green-600", bar: "bg-green-500", border: "border-green-200" };
  };

  return (
    <div className="space-y-4" data-testid="attendance-module">
      {/* Date Picker Modal */}
      <DatePickerModal
        isOpen={showDatePicker}
        onClose={() => setShowDatePicker(false)}
        month={selectedMonth}
        year={selectedYear}
        onSelectDate={handleDateSelect}
        monthNames={monthNamesFull}
        attendanceData={attendanceData}
      />
      
      {/* Override Modal */}
      <OverrideModal
        isOpen={showOverrideModal}
        onClose={() => setShowOverrideModal(false)}
        child={overrideData.child}
        date={overrideData.date}
        currentStatus={overrideData.status}
        onSave={handleOverride}
        saving={savingOverride}
      />
      
      {/* Mobile-friendly Header */}
      <div className="space-y-3">
        {/* Row 1: Month Navigation + Filter Toggle */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <button
              onClick={goToPrevMonth}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
              data-testid="prev-month-btn"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
            <button
              onClick={() => setShowDatePicker(true)}
              className="px-3 py-1.5 bg-purple-100 rounded-lg font-bold text-purple-700 text-sm hover:bg-purple-200 transition-colors"
              data-testid="month-picker-btn"
            >
              {monthNames[selectedMonth - 1]} {selectedYear}
              {selectedDate && (
                <span className="ml-1 text-purple-500">({selectedDate.split('-')[2]})</span>
              )}
            </button>
            <button
              onClick={goToNextMonth}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
              data-testid="next-month-btn"
            >
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>
          
          {/* Filter & Export Toggle */}
          <div className="flex items-center gap-1">
            {/* Clear date filter if set */}
            {selectedDate && (
              <button
                onClick={() => setSelectedDate(null)}
                className="px-2 py-1 bg-orange-100 hover:bg-orange-200 rounded-lg text-orange-600 text-xs font-medium"
              >
                Reset
              </button>
            )}
            
            {/* Filter button - only show for roles that can filter */}
            {canFilter && (
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${showFilters ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-600'}`}
                data-testid="filter-toggle-btn"
              >
                <Filter className="w-4 h-4" />
              </button>
            )}
            
            {/* Download button - hidden for ibu_bapa */}
            {canDownload && (
              <button
                onClick={() => exportAttendance("excel")}
                disabled={exporting}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-green-100 hover:bg-green-200 text-green-600 transition-colors"
                data-testid="download-btn"
              >
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              </button>
            )}
          </div>
        </div>

        {/* Row 2: Filters (collapsible) - only for roles that can filter */}
        {canFilter && showFilters && (
          <div className="p-3 bg-gray-50 rounded-xl space-y-2">
            <div className="grid grid-cols-2 gap-2">
              {/* Branch Filter - Only for superadmin/branch_manager */}
              {(role === "superadmin" || role === "branch_manager") && (
                <select
                  value={filterBranch}
                  onChange={(e) => setFilterBranch(e.target.value)}
                  className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-200"
                  data-testid="branch-filter"
                >
                  <option value="">Semua Cawangan</option>
                  {Array.isArray(branches) && branches.map(b => (
                    <option key={b.name} value={b.name}>{b.name}</option>
                  ))}
                </select>
              )}
              
              {/* Class Filter - Show all classes */}
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className={`px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-200 ${
                  (role !== "superadmin" && role !== "branch_manager") ? 'col-span-2' : ''
                }`}
                data-testid="class-filter"
              >
                <option value="">Semua Kelas</option>
                {Array.isArray(classes) && classes.map(cls => (
                  <option key={cls.name || cls.id} value={cls.name}>
                    {cls.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Attendance Status Filter */}
            <div className="grid grid-cols-3 gap-1">
              <button
                onClick={() => setAttendanceFilter("all")}
                className={`py-2 rounded-lg text-xs font-bold transition-all ${
                  attendanceFilter === "all" ? "bg-purple-500 text-white" : "bg-white text-gray-600 hover:bg-gray-100 border"
                }`}
                data-testid="filter-all"
              >
                Semua
              </button>
              <button
                onClick={() => setAttendanceFilter("hadir")}
                className={`py-2 rounded-lg text-xs font-bold transition-all ${
                  attendanceFilter === "hadir" ? "bg-green-500 text-white" : "bg-white text-green-600 hover:bg-green-50 border border-green-200"
                }`}
                data-testid="filter-hadir"
              >
                Hadir
              </button>
              <button
                onClick={() => setAttendanceFilter("tak_hadir")}
                className={`py-2 rounded-lg text-xs font-bold transition-all ${
                  attendanceFilter === "tak_hadir" ? "bg-red-500 text-white" : "bg-white text-red-600 hover:bg-red-50 border border-red-200"
                }`}
                data-testid="filter-tak-hadir"
              >
                Tidak Hadir
              </button>
            </div>
            
            {/* Percentage Range Filter */}
            <div>
              <p className="text-xs text-gray-500 mb-1 font-medium">Peratusan Kehadiran:</p>
              <div className="flex flex-wrap gap-1">
                <button
                  onClick={() => setPercentageFilter("all")}
                  className={`px-2 py-1 rounded-lg text-xs font-bold transition-all ${
                    percentageFilter === "all" ? "bg-purple-500 text-white" : "bg-white text-gray-600 hover:bg-gray-100 border"
                  }`}
                  data-testid="pct-filter-all"
                >
                  Semua
                </button>
                <button
                  onClick={() => setPercentageFilter("0-19")}
                  className={`px-2 py-1 rounded-lg text-xs font-bold transition-all ${
                    percentageFilter === "0-19" ? "bg-red-500 text-white" : "bg-white text-red-600 hover:bg-red-50 border border-red-200"
                  }`}
                  data-testid="pct-filter-0-19"
                >
                  0-19%
                </button>
                <button
                  onClick={() => setPercentageFilter("20-39")}
                  className={`px-2 py-1 rounded-lg text-xs font-bold transition-all ${
                    percentageFilter === "20-39" ? "bg-orange-500 text-white" : "bg-white text-orange-600 hover:bg-orange-50 border border-orange-200"
                  }`}
                  data-testid="pct-filter-20-39"
                >
                  20-39%
                </button>
                <button
                  onClick={() => setPercentageFilter("40-59")}
                  className={`px-2 py-1 rounded-lg text-xs font-bold transition-all ${
                    percentageFilter === "40-59" ? "bg-yellow-500 text-white" : "bg-white text-yellow-600 hover:bg-yellow-50 border border-yellow-200"
                  }`}
                  data-testid="pct-filter-40-59"
                >
                  40-59%
                </button>
                <button
                  onClick={() => setPercentageFilter("60-79")}
                  className={`px-2 py-1 rounded-lg text-xs font-bold transition-all ${
                    percentageFilter === "60-79" ? "bg-blue-500 text-white" : "bg-white text-blue-600 hover:bg-blue-50 border border-blue-200"
                  }`}
                  data-testid="pct-filter-60-79"
                >
                  60-79%
                </button>
                <button
                  onClick={() => setPercentageFilter("80-100")}
                  className={`px-2 py-1 rounded-lg text-xs font-bold transition-all ${
                    percentageFilter === "80-100" ? "bg-green-500 text-white" : "bg-white text-green-600 hover:bg-green-50 border border-green-200"
                  }`}
                  data-testid="pct-filter-80-100"
                >
                  80-100%
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Row 3: View Mode Toggle - hide for ibu_bapa */}
        {role !== "ibu_bapa" && (
        <div className="flex bg-gray-100 rounded-xl p-1">
          <button
            onClick={() => setViewMode("list")}
            className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-1 ${viewMode === "list" ? "bg-white shadow text-purple-600" : "text-gray-500"}`}
            data-testid="list-view-btn"
          >
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Senarai</span>
          </button>
          <button
            onClick={() => { setViewMode("calendar"); setSelectedChild(null); }}
            className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-1 ${viewMode === "calendar" ? "bg-white shadow text-purple-600" : "text-gray-500"}`}
            data-testid="calendar-view-btn"
          >
            <CalendarDays className="w-4 h-4" />
            <span className="hidden sm:inline">Kalendar</span>
          </button>
        </div>
        )}
      </div>

      {/* Current Filter Info - only show for roles that can filter */}
      {canFilter && (filterBranch || selectedClass || selectedDate || attendanceFilter !== "all" || percentageFilter !== "all") && (
        <div className="flex flex-wrap gap-2 text-xs">
          {filterBranch && filterBranch !== "all" && (
            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
              📍 {filterBranch}
            </span>
          )}
          {selectedClass && (
            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full">
              🎓 {selectedClass.split('(')[0].trim()}
            </span>
          )}
          {selectedDate && (
            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
              📅 {selectedDate}
            </span>
          )}
          {attendanceFilter !== "all" && (
            <span className={`px-2 py-1 rounded-full ${attendanceFilter === "hadir" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
              {attendanceFilter === "hadir" ? "Hadir" : "Tidak Hadir"}
            </span>
          )}
          {percentageFilter !== "all" && (
            <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full">
              {percentageFilter}%
            </span>
          )}
        </div>
      )}

      {/* Stats Cards - Responsive Grid */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        <StatsCard
          icon={Users}
          label="Kanak-kanak"
          value={stats.total_children || 0}
          color="border-blue-500"
        />
        <StatsCard
          icon={CalendarDays}
          label="Hari Sekolah"
          value={stats.total_school_days || 0}
          color="border-purple-500"
        />
        <StatsCard
          icon={CheckCircle2}
          label="Jumlah Hari Hadir"
          value={stats.total_present || 0}
          color="border-green-500"
        />
        <StatsCard
          icon={BarChart3}
          label="Purata"
          value={`${stats.average_attendance_rate || 0}%`}
          color="border-orange-500"
        />
      </div>

      {/* Today's Attendance Summary - Show when Hadir/Tidak Hadir filter active */}
      {(attendanceFilter === "hadir" || attendanceFilter === "tak_hadir") && (
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 border-2 border-purple-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-gray-800 flex items-center gap-2">
              {attendanceFilter === "hadir" ? (
                <>
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span>Ringkasan Kehadiran {selectedDate ? `(${selectedDate})` : 'Hari Ini'}</span>
                </>
              ) : (
                <>
                  <XCircle className="w-5 h-5 text-red-600" />
                  <span>Ringkasan Ketidakhadiran {selectedDate ? `(${selectedDate})` : 'Hari Ini'}</span>
                </>
              )}
            </h3>
          </div>

          {/* Total Summary */}
          <div className={`mb-3 p-3 rounded-lg ${attendanceFilter === "hadir" ? "bg-green-100" : "bg-red-100"}`}>
            <div className="flex items-center justify-between">
              <span className={`text-sm font-medium ${attendanceFilter === "hadir" ? "text-green-700" : "text-red-700"}`}>
                {attendanceFilter === "hadir" ? "Jumlah Hadir:" : "Jumlah Tidak Hadir:"}
              </span>
              <span className={`text-2xl font-black ${attendanceFilter === "hadir" ? "text-green-600" : "text-red-600"}`}>
                {attendanceFilter === "hadir" ? todayStats.totalPresent : todayStats.totalAbsent} orang
              </span>
            </div>
          </div>

          {/* By Class Breakdown */}
          <div>
            <p className="text-xs font-medium text-gray-600 mb-2">Pecahan Mengikut Kelas:</p>
            <div className="space-y-2">
              {Object.entries(todayStats.byClass)
                .filter(([className, classStats]) => 
                  attendanceFilter === "hadir" ? classStats.present > 0 : classStats.absent > 0
                )
                .sort((a, b) => {
                  const valA = attendanceFilter === "hadir" ? a[1].present : a[1].absent;
                  const valB = attendanceFilter === "hadir" ? b[1].present : b[1].absent;
                  return valB - valA;
                })
                .map(([className, classStats]) => (
                  <div key={className} className="flex items-center justify-between bg-white rounded-lg p-2 shadow-sm">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <GraduationCap className="w-4 h-4 text-purple-500 flex-shrink-0" />
                      <span className="text-sm text-gray-700 truncate">{className}</span>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {attendanceFilter === "hadir" ? (
                        <>
                          <span className="text-lg font-black text-green-600">{classStats.present}</span>
                          <span className="text-xs text-gray-400">dari {classStats.total}</span>
                        </>
                      ) : (
                        <>
                          <span className="text-lg font-black text-red-600">{classStats.absent}</span>
                          <span className="text-xs text-gray-400">dari {classStats.total}</span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
            </div>

            {/* Show message if no data */}
            {Object.entries(todayStats.byClass).filter(([className, classStats]) => 
              attendanceFilter === "hadir" ? classStats.present > 0 : classStats.absent > 0
            ).length === 0 && (
              <div className="text-center py-4 text-gray-500 text-sm">
                {attendanceFilter === "hadir" 
                  ? "Tiada kanak-kanak hadir pada tarikh ini" 
                  : "Tiada kanak-kanak tidak hadir pada tarikh ini"}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Attendance Rate Color Legend */}
      <div className="flex flex-wrap items-center gap-2 px-1 text-xs">
        <span className="text-gray-500 font-medium">Petunjuk:</span>
        <div className="flex items-center gap-1"><div className="w-3 h-3 bg-red-500 rounded" /><span className="text-gray-600">0-19%</span></div>
        <div className="flex items-center gap-1"><div className="w-3 h-3 bg-orange-500 rounded" /><span className="text-gray-600">20-39%</span></div>
        <div className="flex items-center gap-1"><div className="w-3 h-3 bg-yellow-500 rounded" /><span className="text-gray-600">40-59%</span></div>
        <div className="flex items-center gap-1"><div className="w-3 h-3 bg-blue-500 rounded" /><span className="text-gray-600">60-79%</span></div>
        <div className="flex items-center gap-1"><div className="w-3 h-3 bg-green-500 rounded" /><span className="text-gray-600">80-100%</span></div>
      </div>

      {/* Attendance List View */}
      {viewMode === "list" && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Mobile Card View */}
          <div className="divide-y divide-gray-100">
            {filteredAttendance.map((child) => {
              const colors = getAttendanceColor(child.attendance_rate || 0);
              return (
              <div key={child.child_id} className={`p-3 hover:bg-gray-50 ${colors.bg} ${colors.border} border-l-4`}>
                <div className="flex items-center gap-3">
                  {/* Photo */}
                  {child.photo_url ? (
                    <img src={child.photo_url} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-bold flex-shrink-0">
                      {child.child_name?.charAt(0)}
                    </div>
                  )}
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 truncate">{child.child_name}</p>
                    <p className="text-xs text-gray-500 truncate">{child.class_name || "Tiada kelas"}</p>
                  </div>
                  
                  {/* Stats */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="text-center">
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-bold text-green-600">{child.present_count}</span>
                        <span className="text-gray-300">/</span>
                        <span className="text-xs font-bold text-red-500">{child.absent_count}</span>
                      </div>
                      <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${colors.bar} rounded-full`} 
                          style={{ width: `${child.attendance_rate}%` }}
                        />
                      </div>
                    </div>
                    
                    {/* Override button for authorized roles */}
                    {canOverride && (
                      <button
                        onClick={() => {
                          setOverrideData({
                            child: { id: child.child_id, name: child.child_name },
                            date: new Date().toISOString().split('T')[0],
                            status: true
                          });
                          setShowOverrideModal(true);
                        }}
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-50 text-orange-600 hover:bg-orange-100"
                        title="Override Kehadiran"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                    )}
                    
                    <button
                      onClick={() => {
                        setViewMode("calendar");
                        fetchCalendarData(child.child_id);
                      }}
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-purple-50 text-purple-600 hover:bg-purple-100"
                    >
                      <Calendar className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
          
          {filteredAttendance.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">
                {(attendanceFilter !== "all" || percentageFilter !== "all") 
                  ? "Tiada data kehadiran yang sepadan dengan filter" 
                  : "Tiada data kehadiran"}
              </p>
              {(attendanceFilter !== "all" || percentageFilter !== "all") && (
                <button 
                  onClick={() => { setAttendanceFilter("all"); setPercentageFilter("all"); }}
                  className="mt-2 text-xs text-purple-600 hover:text-purple-800 font-medium"
                >
                  Reset Filter
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Calendar View */}
      {viewMode === "calendar" && (
        <div className="bg-white rounded-xl shadow-sm p-4">
          {selectedChild ? (
            <>
              {/* Child Info Header */}
              <div className="flex items-center gap-3 mb-4 pb-3 border-b">
                <button
                  onClick={() => { setSelectedChild(null); setCalendarData(null); }}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {selectedChild.photo_url ? (
                  <img src={selectedChild.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-bold">
                    {selectedChild.name?.charAt(0)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-800 truncate">{selectedChild.name}</p>
                  <p className="text-xs text-gray-500 truncate">{selectedChild.class_name}</p>
                </div>
                {calendarData?.stats && (
                  <div className="text-right flex-shrink-0">
                    <p className="text-lg font-black text-green-600">{calendarData.stats.attendance_rate}%</p>
                    <p className="text-xs text-gray-500">{calendarData.stats.present_count}/{calendarData.stats.total_school_days}</p>
                  </div>
                )}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {["A", "I", "S", "R", "K", "J", "Sa"].map(day => (
                  <div key={day} className="text-center text-xs font-bold text-gray-400 py-1">
                    {day}
                  </div>
                ))}
              </div>
              
              <div className="grid grid-cols-7 gap-1">
                {calendarData?.calendar && Array.from({ 
                  // Calendar starts on Sunday (0 = Sunday)
                  length: new Date(selectedYear, selectedMonth - 1, 1).getDay()
                }).map((_, i) => (
                  <div key={`empty-${i}`} className="aspect-square" />
                ))}
                
                {calendarData?.calendar?.map((day, idx) => {
                  const today = new Date().toISOString().split('T')[0];
                  return (
                  <CalendarDay
                    key={idx}
                    day={day}
                    isWeekend={day.is_weekend}
                    isPresent={day.present}
                    isToday={day.date === today}
                    isFuture={day.date > today}
                    canOverride={canOverride && !(day.date > today)}
                    onOverride={() => {
                      setOverrideData({
                        child: selectedChild,
                        date: day.date,
                        status: day.present
                      });
                      setShowOverrideModal(true);
                    }}
                  />
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex items-center justify-center gap-4 mt-4 pt-3 border-t text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-green-100 rounded" />
                  <span className="text-gray-500">Hadir</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-red-50 rounded" />
                  <span className="text-gray-500">Tidak Hadir</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-gray-100 rounded" />
                  <span className="text-gray-500">Cuti</span>
                </div>
              </div>
              
              {/* Override hint for authorized roles */}
              {canOverride && (
                <p className="text-center text-xs text-purple-500 mt-2">
                  💡 Klik pada tarikh untuk override kehadiran
                </p>
              )}
            </>
          ) : (
            /* Child Selection Grid */
            <div>
              <p className="text-sm font-medium text-gray-600 mb-3">Pilih kanak-kanak:</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                {filteredAttendance.map(child => {
                  const colors = getAttendanceColor(child.attendance_rate || 0);
                  return (
                  <button
                    key={child.child_id}
                    onClick={() => fetchCalendarData(child.child_id)}
                    className="p-2 bg-gray-50 hover:bg-purple-50 rounded-xl text-center transition-all border border-transparent hover:border-purple-200"
                  >
                    {child.photo_url ? (
                      <img src={child.photo_url} alt="" className="w-10 h-10 rounded-full object-cover mx-auto mb-1" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-bold mx-auto mb-1 text-sm">
                        {child.child_name?.charAt(0)}
                      </div>
                    )}
                    <p className="text-xs font-medium text-gray-700 truncate">{child.child_name?.split(' ')[0]}</p>
                    <p className={`text-xs font-bold ${colors.text}`}>{child.attendance_rate}%</p>
                  </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
